import { useState } from 'react';
import type { AssistClient, RatingRequest } from '../assist/client';
import { normaliseIdentityField } from '../domain/identity';
import { todayInParis } from '../domain/drink-by';
import { onlineRatingFrom, type SavedOnlineRating } from '../domain/online-rating';
import { quotaMessage, t } from '../i18n';
import { GoogleSearchSuggestions } from './GoogleSearchSuggestions';
import { OnlineRatingValue } from './OnlineRatingValue';

/** The wine a result belongs to, so a result for another wine is never shown or saved. */
export const ratingKey = (wine: RatingRequest) =>
  [wine.domaine, wine.cepage, wine.parcelle, wine.annee].map(normaliseIdentityField).join('|');

type Found =
  | { kind: 'rating'; saved: SavedOnlineRating; searchSuggestions?: string }
  | { kind: 'none' }
  | { kind: 'failed'; message: string };

/**
 * "Note en ligne" (owner requests, 2026-10-03): only when tapped, asks the `assist` function to
 * look the wine up on wine marketplaces and review sites (Vivino and others) and turns what it
 * finds into one rating on the app's 5-point scale (domain/online-rating.ts), with each site's
 * rating and the day for the wine's notes, handed to `onFound` to be saved on the wine. With
 * `showResult` false the page shows the saved rating itself. Google's search suggestions, which
 * its terms require with a search's result, follow. Needs the Domaine, the Cépage and the Année.
 * On the purchase form, "Recherche en ligne" finds the rating instead (components/WebCompletion).
 */
export function OnlineRatingControl({
  client,
  wine,
  onFound,
  showResult = true,
}: {
  client: AssistClient;
  wine: RatingRequest;
  onFound?: (saved: SavedOnlineRating, key: string) => void;
  showResult?: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState<{ key: string; found: Found } | undefined>();
  const ready =
    wine.domaine.trim() !== '' && wine.cepage.trim() !== '' && /^\d{4}$/.test(wine.annee.trim());
  const shown = found && found.key === ratingKey(wine) ? found.found : undefined;

  async function ask() {
    setBusy(true);
    const key = ratingKey(wine);
    const answer = await client.rating(wine);
    // With the day and each site's rating for the wine's notes (owner, 2026-10-03).
    const saved = answer.ok ? onlineRatingFrom(answer.ratings, todayInParis()) : undefined;
    const result: Found =
      answer.ok && saved
        ? {
            kind: 'rating',
            saved,
            ...(answer.searchSuggestions ? { searchSuggestions: answer.searchSuggestions } : {}),
          }
        : answer.ok
          ? { kind: 'none' }
          : {
              kind: 'failed',
              message:
                'reason' in answer ? quotaMessage(answer.period) : t('rating.onlineUnavailable'),
            };
    setFound({ key, found: result });
    setBusy(false);
    if (result.kind === 'rating') onFound?.(result.saved, key);
  }

  return (
    <div className="online-rating">
      <button
        type="button"
        className="cta-action"
        disabled={busy || !ready}
        onClick={() => void ask()}
      >
        {busy ? t('rating.onlineSearching') : t('rating.online')}
      </button>
      {shown?.kind === 'failed' && (
        <p role="alert" className="suggestions__message">
          {shown.message}
        </p>
      )}
      {shown?.kind === 'none' && (
        <p role="status" className="suggestions__message">
          {t('rating.onlineNone')}
        </p>
      )}
      {shown?.kind === 'rating' && showResult && (
        <p className="online-rating__found">
          <OnlineRatingValue saved={shown.saved} />
        </p>
      )}
      {shown?.kind === 'rating' && shown.searchSuggestions && (
        <GoogleSearchSuggestions html={shown.searchSuggestions} />
      )}
    </div>
  );
}
